import React from 'react';
import { useTranslation } from 'react-i18next';

// Comparison charts for Fundraising. Venture-Fund raises and blockchain/web3
// project rounds are shown in SEPARATE charts (never combined). Each chart is a
// single series → magnitude, so one hue and no legend (the title names it).

const VF_COLOR = '#10b981';   // Venture Fund (green)
const PROJ_COLOR = '#3b82f6'; // Web3 project (blue)

function fmtUsd(n) {
  if (n == null || !Number.isFinite(n)) return '—';
  if (n >= 1e9) return `$${(n / 1e9).toFixed(2)}B`;
  if (n >= 1e6) return `$${(n / 1e6).toFixed(1)}M`;
  if (n >= 1e3) return `$${(n / 1e3).toFixed(0)}K`;
  return `$${n.toFixed(0)}`;
}
const short = (s, n = 22) => (String(s).length > n ? String(s).slice(0, n - 1) + '…' : String(s));

// Horizontal bars — compare entities by magnitude. Direct value labels; native
// <title> tooltip per bar; recessive baseline.
function HBarChart({ title, subtitle, items, color, note }) {
  const { t } = useTranslation();
  const rows = items.slice(0, 12);
  const max = Math.max(1, ...rows.map(r => r.value));
  const rowH = 40, gap = 12, labelW = 240, valueW = 130, padX = 10, padTop = 8;
  const barArea = 1000 - labelW - valueW - padX * 2;
  const H = padTop + rows.length * (rowH + gap);

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <span style={s.cardTitle}>{title}</span>
        {subtitle && <span style={s.cardSub}>{subtitle}</span>}
      </div>
      {rows.length === 0 ? (
        <div style={s.empty}>{t('fundraisingCharts.noData')}</div>
      ) : (
        <div style={s.svgScroll}>
          <svg viewBox={`0 0 1000 ${H}`} width="100%" style={{ minWidth: 520, display: 'block' }}>
            {rows.map((r, i) => {
              const y = padTop + i * (rowH + gap);
              const w = Math.max(2, (r.value / max) * barArea);
              return (
                <g key={r.name}>
                  <text x={padX + labelW - 10} y={y + rowH / 2 + 6} textAnchor="end" style={s.barLabel}>{short(r.name, 22)}</text>
                  <rect x={padX + labelW} y={y} width={barArea} height={rowH} rx="4" fill="var(--border)" fillOpacity="0.35" />
                  <rect x={padX + labelW} y={y} width={w} height={rowH} rx="4" fill={color}>
                    <title>{r.name}: {fmtUsd(r.value)}{r.deals ? ` · ${r.deals} ${t('fundraisingCharts.vfDeals')}` : ''}</title>
                  </rect>
                  <text x={padX + labelW + w + 10} y={y + rowH / 2 + 6} style={s.barValue}>{fmtUsd(r.value)}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
      {note && <div style={s.note}>{note}</div>}
    </div>
  );
}

// Monthly columns — one metric over time (deal count). Recessive axis; direct
// labels only where they fit; <title> tooltip per column.
function MonthlyColumns({ title, subtitle, data, valueKey, color }) {
  const { t } = useTranslation();
  const rows = data.slice(-12);
  const max = Math.max(1, ...rows.map(d => d[valueKey]));
  const W = 1000, H = 300, padL = 10, padR = 10, padTop = 26, padBottom = 44;
  const plotW = W - padL - padR, plotH = H - padTop - padBottom;
  const colW = rows.length ? plotW / rows.length : 0;
  const barW = Math.min(60, colW * 0.6);

  return (
    <div style={s.card}>
      <div style={s.cardHead}>
        <span style={s.cardTitle}>{title}</span>
        {subtitle && <span style={s.cardSub}>{subtitle}</span>}
      </div>
      {rows.length === 0 ? (
        <div style={s.empty}>{t('fundraisingCharts.noData')}</div>
      ) : (
        <div style={s.svgScroll}>
          <svg viewBox={`0 0 ${W} ${H}`} width="100%" style={{ minWidth: 520, display: 'block' }}>
            <line x1={padL} y1={padTop + plotH} x2={W - padR} y2={padTop + plotH} stroke="var(--border)" strokeWidth="1" />
            {rows.map((d, i) => {
              const v = d[valueKey] || 0;
              const h = Math.max(1, (v / max) * plotH);
              const x = padL + i * colW + (colW - barW) / 2;
              const y = padTop + plotH - h;
              return (
                <g key={d.month}>
                  <rect x={x} y={y} width={barW} height={h} rx="4" fill={color}>
                    <title>{d.month}: {v} {t('fundraisingCharts.monthlyRounds')}</title>
                  </rect>
                  {v > 0 && <text x={x + barW / 2} y={y - 6} textAnchor="middle" style={s.colValue}>{v}</text>}
                  <text x={x + barW / 2} y={padTop + plotH + 26} textAnchor="middle" style={s.colLabel}>{d.month.slice(2)}</text>
                </g>
              );
            })}
          </svg>
        </div>
      )}
    </div>
  );
}

export default function FundraisingCharts({ data }) {
  const { t } = useTranslation();
  if (!data) return null;
  const { ventureFunds = [], projects = [], frequency = [] } = data;

  return (
    <div style={s.wrap}>
      <div style={s.sectionLabel}>{t('fundraisingCharts.sectionSize')}</div>
      <div style={s.grid2}>
        <HBarChart
          title={t('fundraisingCharts.vfTitle')}
          subtitle={t('fundraisingCharts.vfSubtitle')}
          items={ventureFunds.map(f => ({ name: f.name, value: f.raised, deals: f.deals }))}
          color={VF_COLOR}
          note={t('fundraisingCharts.vfNote', { count: ventureFunds.length })}
        />
        <HBarChart
          title={t('fundraisingCharts.projTitle')}
          subtitle={t('fundraisingCharts.projSubtitle')}
          items={projects.map(p => ({ name: p.name, value: p.raised, deals: p.deals }))}
          color={PROJ_COLOR}
          note={t('fundraisingCharts.projNote', { count: projects.length })}
        />
      </div>

      <div style={s.sectionLabel}>{t('fundraisingCharts.sectionFrequency')}</div>
      <div style={s.grid2}>
        <MonthlyColumns title={t('fundraisingCharts.vfMonthlyTitle')} subtitle={t('fundraisingCharts.vfMonthlySubtitle')} data={frequency} valueKey="ventureCount" color={VF_COLOR} />
        <MonthlyColumns title={t('fundraisingCharts.projMonthlyTitle')} subtitle={t('fundraisingCharts.projMonthlySubtitle')} data={frequency} valueKey="projectCount" color={PROJ_COLOR} />
      </div>
    </div>
  );
}

const s = {
  wrap: { display: 'flex', flexDirection: 'column', gap: 8 },
  sectionLabel: { fontSize: 11, fontWeight: 800, color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: 0.6, marginTop: 6 },
  grid2: { display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(340px, 1fr))', gap: 12 },
  card: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 14, padding: '14px 16px' },
  cardHead: { display: 'flex', flexDirection: 'column', gap: 2, marginBottom: 10 },
  cardTitle: { fontSize: 15, fontWeight: 800, color: 'var(--text)' },
  cardSub: { fontSize: 12, color: 'var(--text-muted)' },
  svgScroll: { overflowX: 'auto' },
  empty: { fontSize: 13, color: 'var(--text-muted)', padding: '20px 0', textAlign: 'center' },
  note: { fontSize: 11, color: 'var(--text-dim)', marginTop: 6 },
  // SVG text — sized in the 1000-wide viewBox, so ~2x the visual px to stay
  // crisp once the chart scales down inside a 2-column card.
  barLabel: { fontSize: 20, fontWeight: 600, fill: 'var(--text)' },
  barValue: { fontSize: 20, fontWeight: 700, fill: 'var(--text)', fontVariantNumeric: 'tabular-nums' },
  colValue: { fontSize: 18, fontWeight: 700, fill: 'var(--text-muted)', fontVariantNumeric: 'tabular-nums' },
  colLabel: { fontSize: 16, fill: 'var(--text-dim)' },
};
